import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useItemCategoryMutations, useUnitMutations } from '@/features/inventory/api';
import { Modal } from '@/features/inventory/modal';
import { useCreatePartner } from '@/features/partners/api';
import { localizeServerMessage } from '@/lib/server-messages';

import type { PickerEntity } from './roster';

/**
 * D26's "small in-place window" — the SIMPLE half of the ruling.
 *
 * ── THE ONE RULE THIS FILE EXISTS TO KEEP ─────────────────────────────────
 *
 * **It submits through the SAME server contract as the full form.** Not a
 * parallel create route, not a relaxed schema, not a "quick" variant of
 * anything. Each form below calls the exact mutation hook its full screen
 * calls — `useCreatePartner`, `useItemCategoryMutations().create`,
 * `useUnitMutations().create` — so the server applies identical validation,
 * identical permission checks and identical business rules to a record made
 * here and a record made there. A second create path is how two screens start
 * disagreeing about what a valid customer is, and the disagreement surfaces
 * months later as data nobody can explain.
 *
 * The fields each window shows are therefore not a design choice but a
 * MEASUREMENT of the create schema: exactly the fields the server requires and
 * cannot default. Where the schema requires something it cannot default and
 * the full form supplies a default of its own, that default is used and named
 * in `roster.ts` — the unit's `precision: 0` is the only such case, and it is
 * the full form's own `EMPTY` value rather than a number chosen here.
 *
 * ── CANCEL LEAVES NOTHING BEHIND ──────────────────────────────────────────
 *
 * There is no draft, no pre-created record and no id reserved before Save.
 * The window holds its fields in local state and the mutation is the first
 * thing that touches the server, so Cancel, Escape and the backdrop all leave
 * a tree with one fewer record than the optimistic version would — no ghosts.
 * That is the whole reason the picker's field is NOT changed when the create
 * row is chosen (see `chooseCreate` in `components/ui/typeahead.tsx`): the
 * field moves when a record exists, and at no other moment.
 *
 * ── WHY IT IS DISMISSIBLE ─────────────────────────────────────────────────
 *
 * `Modal` can refuse the backdrop and Escape, and two screens in PART 9 and
 * MFG-04 ask it to because "it is a data-entry form and it must be hard to
 * lose by accident". Those are long forms holding real work. This window holds
 * at most three boxes that take seconds to retype, and taking Escape away from
 * a keyboard user to protect three seconds of typing is the worse trade — a
 * modal a keyboard user cannot dismiss is the thing that makes them reload
 * the page. Recorded rather than defaulted into, because the precedent points
 * the other way and somebody will ask.
 */
export function QuickCreateWindow({
  entity,
  titleKey,
  onCreated,
  onClose,
}: {
  entity: PickerEntity;
  titleKey: string;
  /** The new record's id. The picker selects it; the window closes. */
  onCreated: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Modal title={t(titleKey)} onClose={onClose} className="max-w-md">
      {entity === 'customer' || entity === 'supplier' ? (
        <PartnerQuickForm entity={entity} onCreated={onCreated} onClose={onClose} />
      ) : entity === 'unit' ? (
        <UnitQuickForm onCreated={onCreated} onClose={onClose} />
      ) : (
        <CategoryQuickForm onCreated={onCreated} onClose={onClose} />
      )}
    </Modal>
  );
}

/**
 * The shell every quick form wears, so the three of them cannot drift into
 * three different button orders or three different places to read an error.
 *
 * `<form onSubmit>` rather than a click handler on Save, because Enter in a
 * one-box form is how people actually submit it, and a window whose only
 * field accepts Enter by doing nothing feels broken. `type="button"` on
 * Cancel is what stops it submitting too.
 */
function QuickForm({
  canSave,
  busy,
  error,
  onSubmit,
  onClose,
  children,
}: {
  canSave: boolean;
  busy: boolean;
  error: string | null;
  onSubmit: () => void;
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <form
      className="grid gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        // The window is rendered inside the picker's own screen, which is
        // usually a `<form>` too (the item form). React bubbles `submit`
        // through the component tree, so without this the window's Save also
        // submitted the ITEM — saving it mid-edit and leaving the screen
        // before the new unit/category could be picked (bugs: New Item #1/#2).
        event.stopPropagation();
        if (canSave && !busy) onSubmit();
      }}
    >
      {children}
      {error === null ? null : (
        // `role="alert"` so a refusal that arrives after the button was
        // pressed is ANNOUNCED rather than merely drawn — the reader's focus
        // is on the button they just pressed, not on this line.
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={!canSave || busy}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}

/** The refusal sentence, through the product's one localiser. */
function asMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * MD-01's minimum: a name. The TYPE is not a box the user ticks — it is
 * decided by the picker that opened this window, so a supplier picker makes a
 * supplier and a customer picker makes a customer. Asking would be offering
 * the user a way to create a record their own field then cannot select.
 */
function PartnerQuickForm({
  entity,
  onCreated,
  onClose,
}: {
  entity: 'customer' | 'supplier';
  onCreated: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const fieldId = useId();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreatePartner();

  return (
    <QuickForm
      canSave={name.trim() !== ''}
      busy={create.isPending}
      error={error}
      onClose={onClose}
      onSubmit={() => {
        setError(null);
        create
          .mutateAsync({
            name: name.trim(),
            isCustomer: entity === 'customer',
            isSupplier: entity === 'supplier',
          })
          .then((result) => onCreated(result.partner.id))
          .catch((cause: unknown) => setError(localizeServerMessage(t, asMessage(cause))));
      }}
    >
      <div className="grid gap-1.5">
        <Label htmlFor={fieldId}>{t('partners.fName')}</Label>
        <Input
          id={fieldId}
          autoFocus
          maxLength={120}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
    </QuickForm>
  );
}

/**
 * MD-04's minimum: a name. The code is "Auto" here (CAT-02) — the full form's
 * own default for a new category — so the window sends no code and the server
 * allocates the next CAT-###. `sortOrder` defaults in the schema.
 */
function CategoryQuickForm({
  onCreated,
  onClose,
}: {
  onCreated: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const nameId = useId();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { create } = useItemCategoryMutations();

  return (
    <QuickForm
      canSave={name.trim() !== ''}
      busy={create.isPending}
      error={error}
      onClose={onClose}
      onSubmit={() => {
        setError(null);
        create
          // `sortOrder: 0` is not a value chosen here: `categories-page.tsx`
          // sends exactly that literal on every create, because the schema's
          // `.default(0)` is a PARSE default and the generated request type
          // still demands the field. Same body, same number.
          .mutateAsync({ name: name.trim(), sortOrder: 0 })
          .then((category) => onCreated(category.id))
          .catch((cause: unknown) => setError(localizeServerMessage(t, asMessage(cause))));
      }}
    >
      <div className="grid gap-1.5">
        <Label htmlFor={nameId}>{t('cats.fName')}</Label>
        <Input
          id={nameId}
          autoFocus
          maxLength={80}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
    </QuickForm>
  );
}

/**
 * MD-12's minimum: a name and a symbol. `precision` is required by the schema
 * with no default of its own, and 0 is what `units-page.tsx`'s own `EMPTY`
 * state puts in a new unit's box — so this window applies the full form's
 * default rather than inventing one. See `roster.ts` for why that is the line
 * between a `window` entity and a `jump` one.
 */
function UnitQuickForm({
  onCreated,
  onClose,
}: {
  onCreated: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const nameId = useId();
  const symbolId = useId();
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { create } = useUnitMutations();

  return (
    <QuickForm
      canSave={name.trim() !== '' && symbol.trim() !== ''}
      busy={create.isPending}
      error={error}
      onClose={onClose}
      onSubmit={() => {
        setError(null);
        create
          // All three defaults are `units-page.tsx`'s own `EMPTY` state —
          // `precision: '0'`, `allowSales: true`, `allowPurchases: true` —
          // which is what the full form submits for a new unit the user did
          // not touch those controls on. Nothing here is invented.
          .mutateAsync({
            name: name.trim(),
            symbol: symbol.trim(),
            precision: 0,
            allowSales: true,
            allowPurchases: true,
          })
          .then((unit) => onCreated(unit.id))
          .catch((cause: unknown) => setError(localizeServerMessage(t, asMessage(cause))));
      }}
    >
      <div className="grid gap-1.5">
        <Label htmlFor={nameId}>{t('units.fName')}</Label>
        <Input
          id={nameId}
          autoFocus
          maxLength={60}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={symbolId}>{t('units.fSymbol')}</Label>
        {/* `dir="ltr"` matches the full form: a unit symbol is a code, and a
            code reads left-to-right even on an Arabic screen. */}
        <Input
          id={symbolId}
          dir="ltr"
          maxLength={10}
          value={symbol}
          onChange={(event) => setSymbol(event.target.value)}
        />
      </div>
    </QuickForm>
  );
}
